/**
 * NormalizationTable - Tabla principal de normalización P1
 * 
 * Columnas: #, Infraestructura, Pin, Tipología, Coordenadas (X+Y), Score
 * Filas sin coordenadas con fondo rojo y borde izquierdo.
 * Hover: línea teal vertical izquierda + fondo destacado.
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 1.3.0 - Redistribución columnas: +80px Infraestructura, -30px Pin/Coords, -20px Score
 * @version 1.6.0 - P1DOS (24-sep-2026): Infraestructura en dos líneas (nombre / dirección);
 *                  tarjeta de hover solo si algo no cupo (truncado u original distinto)
 */

import React, { useState, type FC, type CSSProperties } from 'react';
import {
  colors,
  typography,
  borderRadius,
  transitions,
} from '@/design-system/v2';
import { TypeBadge, type InfrastructureType } from '../shared/TypeBadge';
import { ScoreCell } from '../shared/ScoreCell';
import { TypeDropdown, type TypologyFilter } from './TypeDropdown';
import { fromUtm } from '@/utils/projections';
import {
  type ProcedenciaVia,
  VIA_LABELS,
  VIA_LABELS_LARGO,
} from '@/utils/procedenciaVia';


// ============================================================================
// TYPES
// ============================================================================

export interface NormalizationRow {
  id: number;
  /** Nombre normalizado */
  name: string;
  /** Dirección normalizada */
  address?: string;
  /** Nombre original del documento (antes de normalizar) */
  originalName?: string;
  /** Dirección original del documento (antes de normalizar) */
  originalAddress?: string;
  /** AFOROP1: aforo declarado en el documento (recortado del nombre); va en la segunda línea */
  aforo?: number;
  /** AFOROP1: unidad del aforo; se enseña solo si no es «personas» */
  aforoUnidad?: string;
  type: InfrastructureType;
  x?: number | null;
  y?: number | null;
  originalScore?: number | null;
  newScore?: number | null;
  /** P1-S4 (§B): vía de resolución de coordenadas (5 valores) */
  procedencia?: ProcedenciaVia;
  /** P1-S4 (§B): motivo del residuo cuando procedencia === 'sin_geo' */
  motivoSinVia?: string;
  /**
   * P1PROV-b (DOP-2 a): la fila entra en la geolocalización y aún no ha
   * terminado. Su nota y su procedencia son las de la lectura, no las finales:
   * la tabla enseña la coordenada del documento en gris, sin procedencia, y
   * «…» en vez de nota; los filtros por nota no la cuentan (DOP-3 a).
   */
  pendiente?: boolean;
}

export interface NormalizationTableProps {
  /** Datos de la tabla */
  data: NormalizationRow[];
  /** Filtro de tipología activo */
  typeFilter?: TypologyFilter;
  /** Callback al cambiar filtro de tipología */
  onTypeFilterChange?: (type: TypologyFilter) => void;
  /** Callback al hacer clic en pin */
  onPinClick?: (row: NormalizationRow) => void;
  /** Callback al copiar coordenadas */
  onCopyCoords?: (row: NormalizationRow) => void;
}

// ============================================================================
// MAP URL — Google Maps (única opción tras retirada de IGN Iberpix · OBS-FN-07)
// ============================================================================

const GOOGLE_HOVER_BG = 'rgba(229, 115, 115, 0.10)';

export function buildGoogleMapsUrl(wgsLat: number, wgsLng: number): string {
  return `https://www.google.com/maps?q=${wgsLat.toFixed(6)},${wgsLng.toFixed(6)}`;
}

// ============================================================================
// STYLES
// ============================================================================

const STYLES = {
  container: {
    borderRadius: borderRadius.xl,
    overflow: 'visible',
    border: `1px solid ${colors.border.DEFAULT}`,
    background: colors.bg.secondary,
  } as CSSProperties,

  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
    tableLayout: 'fixed' as const,
    fontFamily: typography.fontFamily.sans,
  } as CSSProperties,

  th: {
    background: colors.bg.tertiary,
    padding: '10px 8px',
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.secondary,
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
    fontSize: typography.fontSize.xs,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    textAlign: 'left' as const,
  } as CSSProperties,

  td: {
    padding: '10px 8px',
    borderBottom: `1px solid ${colors.border.light}`,
    verticalAlign: 'middle' as const,
  } as CSSProperties,

  // Columnas — redistribuidas v1.5 (RVU1-F2): columna Vía retirada; sus 90px
  // pasan a Infraestructura (menos truncado de nombre+dirección). La procedencia
  // sobrevive como micropunto de color en el chip de coordenadas.
  colNum: { width: 40, textAlign: 'center' as const },
  colInfra: { width: 402, textAlign: 'left' as const, position: 'relative' as const },
  colPin: { width: 130, textAlign: 'center' as const, position: 'relative' as const },
  colType: { width: 110, textAlign: 'center' as const },
  colCoords: { width: 190, textAlign: 'center' as const }, // -30px (compacto)
  colScore: { width: 80, textAlign: 'center' as const },   // -20px (compacto)

  // Celda Infraestructura — P1DOS (24-sep-2026): dos líneas, nombre arriba
  // (blanco) y dirección completa debajo (gris), cada una truncada con
  // elipsis en su propia línea. Antes iban en una sola línea separadas por
  // «—» y el hover repetía nombre y dirección aunque cupieran enteros.
  // La celda pierde 3 px de padding vertical por lado para que el alto de
  // fila apenas crezca (interlineado 1.2 en las dos líneas).
  infraCellTd: {
    paddingTop: '7px',
    paddingBottom: '7px',
  } as CSSProperties,

  infraCell: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
  } as CSSProperties,

  infraName: {
    display: 'block',
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.primary,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as CSSProperties,

  infraAddress: {
    display: 'block',
    fontSize: typography.fontSize.sm,
    color: colors.fg.muted,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } as CSSProperties,

  // Línea completa dentro de la tarjeta de hover (solo si la fila truncó)
  fullText: {
    fontSize: '12px',
    color: colors.fg.secondary,
  } as CSSProperties,

  // Tarjeta de hover — RVU1-F2: SUPERPUESTA (position absolute), fuera de
  // flujo → alto de fila invariante (OBS-P1-ROW-HOVER-LAYOUT-SHIFT).
  // P1DOS: solo se enseña cuando la fila no pudo mostrarlo todo — nombre o
  // dirección truncados (medido con scrollWidth > clientWidth al entrar el
  // ratón) o texto original distinto del normalizado — y solo con las
  // líneas que aportan algo (lo truncado entero, lo original si difiere).
  originalRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    position: 'absolute' as const,
    top: 'calc(100% - 8px)',
    left: '8px',
    maxWidth: '380px',
    padding: '6px 10px',
    background: colors.bg.tertiary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.45)',
    zIndex: 30,
    pointerEvents: 'none' as const,
    whiteSpace: 'normal' as const,
  } as CSSProperties,

  originalLabel: {
    fontSize: '10px',
    color: colors.fg.muted,
    fontStyle: 'italic',
    flexShrink: 0,
  } as CSSProperties,

  originalText: {
    fontSize: '12px',
    color: colors.fg.muted,
    fontStyle: 'italic',
  } as CSSProperties,

  // Recuadro unificado para coordenadas X + Copy + Y (compacto)
  coordsWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1px',
    padding: '4px 10px',
    background: 'rgba(45, 212, 191, 0.06)',
    border: `1px solid ${colors.border.light}`,
    borderRadius: borderRadius.md,
    fontFamily: typography.fontFamily.mono,
    fontSize: '13px',
    color: colors.fg.secondary,
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    userSelect: 'none' as const,
  } as CSSProperties,

  coordsWrapperHovered: {
    background: 'rgba(45, 212, 191, 0.11)',
    border: `1px solid rgba(45, 212, 191, 0.45)`,
  } as CSSProperties,

  // P1PROV-b (DOP-2 a): coordenada del documento aún sin comprobar, en gris
  coordsPendiente: {
    color: colors.fg.subtle,
  } as CSSProperties,

  // P1PROV-b (DOP-2 a): «…» sin color en lugar de la nota de lectura
  notaPendiente: {
    display: 'block',
    textAlign: 'center' as const,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    color: colors.fg.subtle,
  } as CSSProperties,

  coordsWrapperEmpty: {
    background: 'transparent',
    border: '1px solid transparent',
    color: colors.fg.subtle,
    width: '100%',
    justifyContent: 'center',
  } as CSSProperties,

  coordValue: {
    minWidth: '72px',
    textAlign: 'right' as const,
    fontSize: '13px',
  } as CSSProperties,

  coordSeparator: {
    color: colors.fg.subtle,
    fontSize: '10px',
  } as CSSProperties,

  utmLabel: {
    fontSize: '9px',
    color: colors.fg.muted,
    fontWeight: typography.fontWeight.medium,
    marginLeft: '2px',
  } as CSSProperties,

  headerSmall: {
    fontSize: '9px',
    color: colors.fg.muted,
    fontWeight: typography.fontWeight.medium,
  } as CSSProperties,

  // Botones
  iconButton: {
    background: 'transparent',
    border: 'none',
    color: colors.fg.muted,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: borderRadius.sm,
    transition: transitions.base,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as CSSProperties,

  iconButtonDisabled: {
    opacity: 0.25,
    cursor: 'not-allowed',
  } as CSSProperties,

  copyButton: {
    background: 'transparent',
    border: 'none',
    color: colors.fg.secondary,
    cursor: 'pointer',
    padding: '2px 3px',
    margin: '0 2px',
    borderRadius: borderRadius.sm,
    transition: transitions.base,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as CSSProperties,

  // Header para dropdown centrado
  typeHeaderCell: {
    position: 'relative' as const,
  } as CSSProperties,
};

// ============================================================================
// ICONS
// ============================================================================

// Pin Google Maps — logo oficial multicolor (con coords) / gris (sin coords).
// Exportado (P2RED-S1-3b): la botonera de Coordenadas de la ficha P2 reutiliza
// el MISMO SVG (decisión M2: icono Google = pin multicolor del P1 vivo).
export const PinGoogleMaps: FC<{ disabled?: boolean }> = ({ disabled = false }) => {
  const grey = '#7A7A7A';
  return (
    <svg
      viewBox="40 25 70 100"
      width={16}
      height={16}
      style={{ opacity: disabled ? 0.4 : 1, display: 'block', flexShrink: 0 }}
      aria-hidden="true"
    >
      <path fill={disabled ? grey : '#1A73E8'} d="M85.1,28.5c-3.2-1-6.6-1.6-10.1-1.6c-10.3,0-19.5,4.7-25.7,12l15.9,13.3L85.1,28.5z"/>
      <path fill={disabled ? grey : '#EA4335'} d="M49.3,38.9c-4.9,5.8-7.9,13.4-7.9,21.6c0,6.3,1.3,11.4,3.3,16l20.4-24.2L49.3,38.9z"/>
      <path fill={disabled ? grey : '#4285F4'} d="M75,47.6c7.1,0,12.8,5.7,12.8,12.8c0,3.2-1.1,6.1-3,8.3c0,0,10.1-12.1,20-23.8c-4.1-7.8-11.1-13.8-19.6-16.5L65.2,52.2C67.5,49.4,71,47.6,75,47.6"/>
      <path fill={disabled ? grey : '#FBBC04'} d="M75,73.3c-7.1,0-12.8-5.7-12.8-12.8c0-3.1,1.1-6,3-8.2L44.8,76.5C48.2,84.2,54,90.4,60,98.2l24.8-29.5C82.5,71.5,78.9,73.3,75,73.3"/>
      <path fill={disabled ? grey : '#34A853'} d="M84.3,106.3c11.2-17.5,24.3-25.5,24.3-45.8c0-5.6-1.4-10.8-3.8-15.5L60,98.2c1.9,2.5,3.8,5.1,5.7,8.1c6.8,10.5,4.9,16.8,9.3,16.8C79.4,123.1,77.5,116.8,84.3,106.3"/>
    </svg>
  );
};

// Pin monocromo para IGN (sin color Google)
const PinIcon: FC<{ color?: string }> = ({ color = 'currentColor' }) => (
  <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);



const CopyIcon: FC = () => (
  <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// ============================================================================
// VIA BADGE (P1-S4 · §B) — badge compacto por vía de resolución
// ============================================================================

const VIA_COLORS: Record<ProcedenciaVia, { color: string; bg: string }> = {
  directa: { color: colors.scores.high, bg: colors.scoresBg.high },
  crs: { color: colors.scores.medium, bg: colors.scoresBg.medium },
  corregida: { color: colors.scores.low, bg: colors.scoresBg.low },
  geocodificada: { color: colors.primary, bg: 'rgba(45, 212, 191, 0.15)' },
  // UINCEN: el centroide municipal no es una ubicación → mismo tono que «corregida», no que «geocod.»
  centroide: { color: colors.scores.low, bg: colors.scoresBg.low },
  sin_geo: { color: colors.scores.critical, bg: colors.scoresBg.critical },
};

interface ViaDotProps {
  via: ProcedenciaVia;
  /** Motivo del residuo (solo sin_geo) — se expone como tooltip nativo */
  motivo?: string;
}

/**
 * RVU1-F2 — micropunto de procedencia.
 *
 * Sustituye al antiguo `ViaBadge` de columna propia: la vía valía «Geocod.» en
 * 50 de 60 filas del corpus Colomera (83 %), de modo que una columna de 90 px
 * transportaba información casi constante. El agregado sigue en el banner de
 * completitud y el detalle por elemento vive en la ficha de P2; aquí basta un
 * punto de color con tooltip, alojado en el chip de coordenadas.
 * La procedencia se conserva íntegra en la exportación (HX-CAPTURA-MAXIMAL).
 */
const ViaDot: FC<ViaDotProps> = ({ via, motivo }) => {
  const { color } = VIA_COLORS[via];
  const tooltip = via === 'sin_geo' && motivo
    ? motivo
    : VIA_LABELS_LARGO[via];
  return (
    <span
      data-testid="via-dot"
      data-via={via}
      title={tooltip}
      aria-label={VIA_LABELS[via]}
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: color,
        flexShrink: 0,
        marginRight: 6,
        display: 'inline-block',
      }}
    />
  );
};

// ============================================================================
// AFOROP1 / P1LISTA · segunda línea de la fila: «dirección / Aforo N p.»
// ============================================================================

/** Texto del aforo para la fila: «Aforo 99 p.» (personas, abreviado); otras unidades enteras: «Aforo 40 plazas». */
export function textoAforo(aforo?: number, unidad?: string): string | undefined {
  if (aforo == null) return undefined;
  const u = unidad && unidad !== 'personas' ? unidad : 'p.';
  return `Aforo ${aforo} ${u}`;
}

/** Segunda línea (gris) de la fila: dirección y aforo, separados por « / »; undefined si no hay nada. */
export function segundaLinea(row: Pick<NormalizationRow, 'address' | 'aforo' | 'aforoUnidad'>): string | undefined {
  const partes = [row.address, textoAforo(row.aforo, row.aforoUnidad)].filter(Boolean);
  return partes.length ? partes.join(' / ') : undefined;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const NormalizationTable: FC<NormalizationTableProps> = ({
  data,
  typeFilter = 'all',
  onTypeFilterChange,
  onPinClick,
  onCopyCoords,
}) => {
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  // P1DOS: qué líneas de la fila bajo el ratón quedaron truncadas (elipsis).
  // Se mide al entrar el ratón, sobre el DOM real: scrollWidth > clientWidth.
  const [truncated, setTruncated] = useState<{ name: boolean; address: boolean }>({
    name: false,
    address: false,
  });
  const [hoveredPin, setHoveredPin] = useState<number | null>(null);
  const [hoveredCopy, setHoveredCopy] = useState<number | null>(null);
  const hasCoords = (row: NormalizationRow) => row.x != null && row.y != null;

  const formatCoord = (value?: number | null): string => {
    if (value == null) return '—';
    return value.toFixed(2);
  };

  // Row hover - background cambia según estado
  const getRowStyle = (row: NormalizationRow, isHovered: boolean): CSSProperties => {
    const noCoords = !hasCoords(row);
    
    // Filas sin coordenadas: fondo rojizo
    if (noCoords) {
      return {
        background: isHovered ? 'rgba(248, 113, 113, 0.08)' : 'rgba(248, 113, 113, 0.04)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      };
    }
    
    // Filas con coordenadas: hover con fondo destacado
    return {
      background: isHovered ? colors.bg.tertiary : 'transparent',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
    };
  };

  // Primera celda con borde izquierdo según estado
  const getFirstCellStyle = (row: NormalizationRow, isHovered: boolean): CSSProperties => {
    const noCoords = !hasCoords(row);
    const baseStyle: CSSProperties = { 
      ...STYLES.td, 
      ...STYLES.colNum,
      transition: 'border-left-color 0.15s ease',
      paddingLeft: '5px',
    };
    
    // Fila sin coordenadas: siempre borde rojo
    if (noCoords) {
      return { 
        ...baseStyle, 
        borderLeft: '3px solid #F87171',
      };
    }
    
    // Fila con coordenadas: borde teal en hover, transparente si no
    return { 
      ...baseStyle, 
      borderLeft: isHovered ? `3px solid ${colors.primary}` : '3px solid transparent',
    };
  };

  const getPinButtonStyle = (row: NormalizationRow, isHovered: boolean): CSSProperties => {
    const disabled = !hasCoords(row);
    return {
      ...STYLES.iconButton,
      ...(disabled ? STYLES.iconButtonDisabled : {}),
      ...(isHovered && !disabled ? { background: GOOGLE_HOVER_BG } : {}),
    };
  };

  return (
    <div style={STYLES.container}>
      <table style={STYLES.table}>
        <thead>
          <tr>
            <th style={{ ...STYLES.th, ...STYLES.colNum, borderTopLeftRadius: '11px' }}>#</th>
            <th style={{ ...STYLES.th, ...STYLES.colInfra }}>Infraestructura</th>
            <th style={{ ...STYLES.th, ...STYLES.colPin }}>
              <span style={{
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.semibold,
                color: colors.fg.secondary,
                textTransform: 'uppercase' as const,
                letterSpacing: '0.5px',
                fontFamily: typography.fontFamily.sans,
                whiteSpace: 'nowrap' as const,
              }}>
                Abrir en <span style={{ color: '#E57373', fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px' }}>Google Maps</span>
              </span>
            </th>
            {/* TypeDropdown centrado en la celda */}
            <th style={{ ...STYLES.th, ...STYLES.colType, ...STYLES.typeHeaderCell }}>
              <TypeDropdown value={typeFilter} onChange={onTypeFilterChange} centered />
            </th>
            {/* Coordenadas unificadas */}
            <th style={{ ...STYLES.th, ...STYLES.colCoords }}>
              <span>Coordenadas</span>
              <span style={STYLES.utmLabel}>(UTM30)</span>
            </th>
            <th style={{ ...STYLES.th, ...STYLES.colScore, borderTopRightRadius: '11px' }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => {
            const isHovered = hoveredRow === row.id;
            const coords = hasCoords(row);
            const hasOriginal = Boolean(row.originalName || row.originalAddress);
            // AFOROP1: la segunda línea lleva dirección y aforo («dirección / Aforo N p.»)
            const linea2 = segundaLinea(row);
            // P1DOS: la tarjeta solo si la fila no pudo mostrarlo todo
            const showCard = isHovered && (truncated.name || truncated.address || hasOriginal);

            return (
              <tr
                key={row.id}
                style={getRowStyle(row, isHovered)}
                onMouseEnter={(e) => {
                  const nameEl = e.currentTarget.querySelector<HTMLElement>('[data-infra-name]');
                  const addrEl = e.currentTarget.querySelector<HTMLElement>('[data-infra-address]');
                  setTruncated({
                    name: !!nameEl && nameEl.scrollWidth > nameEl.clientWidth,
                    address: !!addrEl && addrEl.scrollWidth > addrEl.clientWidth,
                  });
                  setHoveredRow(row.id);
                }}
                onMouseLeave={() => setHoveredRow(null)}
              >
                <td style={getFirstCellStyle(row, isHovered)}>{index + 1}</td>
                {/* RVU1-F2: sin overflow:hidden a nivel de celda — recortaría la
                    tarjeta de hover. El truncado con elipsis lo sigue haciendo
                    `infraCell`, que conserva su propio overflow. */}
                <td style={{ ...STYLES.td, ...STYLES.colInfra, ...STYLES.infraCellTd }}>
                  {/* P1DOS: dos líneas — nombre (blanco) y dirección (gris) */}
                  <div style={STYLES.infraCell}>
                    <span style={STYLES.infraName} data-infra-name="">{row.name}</span>
                    {linea2 && (
                      <span style={STYLES.infraAddress} data-infra-address="">{linea2}</span>
                    )}
                  </div>
                  {/* Tarjeta de hover: siempre en el DOM (display none) para que
                      no salte el alto; visible solo si hay algo que no cupo. */}
                  {(hasOriginal || truncated.name || truncated.address) && (
                    <div
                      data-testid="infra-hover-card"
                      style={{ ...STYLES.originalRow, display: showCard ? 'flex' : 'none' }}
                    >
                      {isHovered && truncated.name && (
                        <span style={STYLES.fullText}>{row.name}</span>
                      )}
                      {isHovered && truncated.address && linea2 && (
                        <span style={STYLES.fullText}>{linea2}</span>
                      )}
                      {/* AFOROP1 (DOP-4 a): «Orig:» solo enseña lo que difiere del documento —
                          el nombre si hay original, la dirección solo si hay dirección original.
                          Nunca repite lo que ya está en la fila. */}
                      {hasOriginal && (
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                          <span style={STYLES.originalLabel}>Orig:</span>
                          <span style={STYLES.originalText}>
                            {row.originalName ?? row.name}
                            {row.originalAddress && (
                              <> — {row.originalAddress}</>
                            )}
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td style={{ ...STYLES.td, ...STYLES.colPin }}>
                  <button
                    style={getPinButtonStyle(row, hoveredPin === row.id)}
                    onClick={() => {
                      if (!coords) return;
                      const wgs = fromUtm(row.x!, row.y!, 'EPSG:25830');
                      window.open(buildGoogleMapsUrl(wgs.lat, wgs.lng), '_blank');
                      onPinClick?.(row);
                    }}
                    onMouseEnter={() => setHoveredPin(row.id)}
                    onMouseLeave={() => setHoveredPin(null)}
                    disabled={!coords}
                    title={coords ? 'Abrir en Google Maps' : 'Sin coordenadas'}
                  >
                    <PinGoogleMaps disabled={!coords} />
                  </button>
                </td>
                <td style={{ ...STYLES.td, ...STYLES.colType }}>
                  <TypeBadge type={row.type} />
                </td>
                {/* Celda unificada: X + Copy + Y */}
                <td style={{ ...STYLES.td, ...STYLES.colCoords }}>
                  {coords ? (
                    <span
                      style={{
                        ...STYLES.coordsWrapper,
                        ...(hoveredCopy === row.id ? STYLES.coordsWrapperHovered : null),
                        ...(row.pendiente ? STYLES.coordsPendiente : null),
                      }}
                      onClick={() => onCopyCoords?.(row)}
                      onMouseEnter={() => setHoveredCopy(row.id)}
                      onMouseLeave={() => setHoveredCopy(null)}
                      title={row.pendiente
                        ? 'Coordenada del documento, sin comprobar. Clic para copiarla'
                        : 'Copiar coordenadas'}
                      data-pendiente={row.pendiente ? 'true' : undefined}
                    >
                      {/* RVU1-F2: micropunto de procedencia (antes columna Vía).
                          P1PROV-b: no en la fila pendiente; la de la lectura cambia al terminar. */}
                      {row.procedencia && !row.pendiente && (
                        <ViaDot via={row.procedencia} motivo={row.motivoSinVia} />
                      )}
                      <span style={STYLES.coordValue}>{formatCoord(row.x)}</span>
                      <span style={{
                        width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: colors.primary,
                        opacity: hoveredCopy === row.id ? 1 : 0,
                        transition: 'opacity 0.15s',
                      }}>
                        <CopyIcon />
                      </span>
                      <span style={STYLES.coordValue}>{formatCoord(row.y)}</span>
                    </span>
                  ) : (
                    <span
                      style={{ ...STYLES.coordsWrapper, ...STYLES.coordsWrapperEmpty }}
                      title={row.pendiente ? 'El documento no trae coordenada; sin comprobar' : undefined}
                      data-pendiente={row.pendiente ? 'true' : undefined}
                    >
                      {/* RVU1-F2: el residuo conserva su punto + motivo en tooltip.
                          P1PROV-b: la fila pendiente aún no es residuo; sin punto. */}
                      {row.procedencia && !row.pendiente && (
                        <ViaDot via={row.procedencia} motivo={row.motivoSinVia} />
                      )}
                      <span style={{ ...STYLES.coordValue, minWidth: 'auto', textAlign: 'center' }}>—</span>
                    </span>
                  )}
                </td>
                <td style={{ ...STYLES.td, ...STYLES.colScore }}>
                  {row.pendiente ? (
                    <span data-testid="nota-pendiente" title="Sin comprobar" style={STYLES.notaPendiente}>…</span>
                  ) : (
                    <ScoreCell originalScore={row.originalScore} newScore={row.newScore} />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default NormalizationTable;
